'use client';

import { useState } from 'react';
import { toast } from 'sonner';
import { useCurrentUser, useLearningSource } from '@akademi/site-builder-runtime';
import type { CourseCardProps } from '../cards/CourseCard.types';

/** Registration outcomes the backend treats as "proceed to the player" —
 *  even {@code APPROVAL_REQUIRED} redirects (the player itself renders
 *  the pending state), matching the legacy card's behavior. */
const SUCCESS_STATUSES = new Set(['REGISTERED', 'APPROVAL_REQUIRED', 'ALREADY_REGISTERED']);

function registrationErrorKey(status: string | undefined): string {
  switch (status) {
    case 'QUOTA_FAILURE':
      return 'message.quotaFailureDuringRegistration';
    case 'USER_CALENDAR_CONFLICT':
      return 'message.userCalendarConflict';
    case 'CLASS_QUOTA_FAILURE':
      return 'message.classQuotaFailureDuringRegistration';
    case 'APPROVAL_REQUIRED':
      return 'message.approvalRequired';
    default:
      return 'message.failedDuringRegistration';
  }
}

/**
 * "Quick Start": register the current user for a course, then jump
 * straight into the player — skipping the course detail page. Mirrors
 * the legacy {@code CourseCardBorderless.quickStart} handler.
 *
 * <p>Falls back to a plain link (the card's own {@code href}) when the
 * host has no registration backend, the viewer is anonymous, or the
 * course carries no {@code playHref} — {@code available} tells the
 * caller when that's the case so it can skip wiring the click handler.
 */
export function useQuickStart(course: CourseCardProps, t: (key: string) => string) {
  const user = useCurrentUser<{ name?: string }>();
  const source = useLearningSource();
  const [loading, setLoading] = useState(false);

  const available = Boolean(user) && Boolean(source?.registerCourse) && Boolean(course.playHref);

  const start = async (e: { preventDefault: () => void }) => {
    if (!available || loading) return;
    e.preventDefault();
    setLoading(true);
    try {
      const res = await source!.registerCourse!(course.id);
      if (SUCCESS_STATUSES.has(res?.status ?? '')) {
        window.location.href = course.playHref!;
        return;
      }
      toast.error(t(registrationErrorKey(res?.status)));
    } catch (err) {
      console.error('[useQuickStart] registration failed', err);
      toast.error(t(registrationErrorKey(undefined)));
    } finally {
      setLoading(false);
    }
  };

  return { available, loading, start };
}
